import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Clock, Users, ChevronLeft, ChevronRight, LogIn, LogOut, RefreshCw, Plus, Save, TicketIcon } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, endOfWeek, parseISO, isToday } from 'date-fns';

const SHIFT_COLORS = {
  morning: 'bg-amber-100 text-amber-700 border-amber-200',
  afternoon_12: 'bg-blue-100 text-blue-700 border-blue-200',
  afternoon_2: 'bg-purple-100 text-purple-700 border-purple-200',
  general: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  off: 'bg-slate-100 text-slate-500 border-slate-200'
};

const SHIFT_LABELS = {
  morning: 'Morning (6 AM)',
  afternoon_12: 'Afternoon (12 PM)',
  afternoon_2: 'Afternoon (2 PM)',
  general: 'General (9 AM)',
  off: 'Day Off'
};

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function ShiftRoster() {
  const { API, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState({});
  const [weeklyRoster, setWeeklyRoster] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(null);
  const [editingRoster, setEditingRoster] = useState({});
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const token = localStorage.getItem('token');
  
  // Check if user is admin or manager
  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const canManage = userRoles.includes('admin') || userRoles.includes('manager');

  useEffect(() => {
    const today = new Date();
    const monday = startOfWeek(today, { weekStartsOn: 1 });
    setCurrentWeekStart(monday);
  }, []);

  useEffect(() => {
    if (currentWeekStart && user) {
      fetchData();
    }
  }, [currentWeekStart, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const weekStartStr = format(currentWeekStart, 'yyyy-MM-dd');
      
      const requests = [
        fetch(`${API}/tickets/shift-templates`),
        fetch(`${API}/tickets/weekly-roster?week_start=${weekStartStr}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/tickets/attendance/today`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ];
      
      if (canManage) {
        requests.push(
          fetch(`${API}/users`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API}/tickets/attendance?start_date=${weekStartStr}&end_date=${format(addDays(currentWeekStart, 6), 'yyyy-MM-dd')}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        );
      }

      const responses = await Promise.all(requests);
      
      const templates = await responses[0].json();
      const roster = await responses[1].json();
      const todayAtt = await responses[2].json();
      
      setShiftTemplates(templates.shifts || {});
      setWeeklyRoster(roster.roster || []);
      setTodayAttendance(todayAtt.attendance);
      
      // Initialize editing roster from fetched data
      const rosterMap = {};
      (roster.roster || []).forEach(r => {
        rosterMap[r.user_email] = {
          monday: r.monday,
          tuesday: r.tuesday,
          wednesday: r.wednesday,
          thursday: r.thursday,
          friday: r.friday,
          saturday: r.saturday,
          sunday: r.sunday
        };
      });
      setEditingRoster(rosterMap);
      
      if (canManage && responses.length > 3) {
        // Check if users API call was successful
        if (responses[3].ok) {
          const usersData = await responses[3].json();
          // Ensure usersData is an array (API returns array directly)
          setUsers(Array.isArray(usersData) ? usersData : []);
        } else {
          console.error('Failed to fetch users:', responses[3].status);
          setUsers([]);
        }
        
        // Check if attendance API call was successful
        if (responses[4].ok) {
          const attendanceData = await responses[4].json();
          setAttendance(attendanceData.attendance || []);
        } else {
          setAttendance([]);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      const res = await fetch(`${API}/tickets/attendance/login`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Login recorded successfully!');
        setTodayAttendance(data.attendance);
      } else {
        toast.info(data.message || 'Already logged in');
      }
    } catch (error) {
      toast.error('Failed to record login');
    }
  };

  const handleLogout = async () => {
    try {
      const res = await fetch(`${API}/tickets/attendance/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Logged out! Total hours: ${data.total_hours || 0}h, Tickets: ${data.tickets_resolved || 0} resolved`);
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to logout');
      }
    } catch (error) {
      toast.error('Failed to record logout');
    }
  };

  const handleShiftChange = (userEmail, day, shift) => {
    setEditingRoster(prev => ({
      ...prev,
      [userEmail]: {
        ...(prev[userEmail] || {}),
        [day]: shift
      }
    }));
  };

  const handleSaveRoster = async () => {
    try {
      const weekStartStr = format(currentWeekStart, 'yyyy-MM-dd');
      const assignments = Object.entries(editingRoster).map(([email, shifts]) => ({
        user_email: email,
        ...shifts
      }));

      const res = await fetch(`${API}/tickets/weekly-roster/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          week_start: weekStartStr,
          assignments
        })
      });

      if (res.ok) {
        toast.success('Roster saved successfully!');
        fetchData();
      } else {
        toast.error('Failed to save roster');
      }
    } catch (error) {
      toast.error('Failed to save roster');
    }
  };

  const navigateWeek = (direction) => {
    setCurrentWeekStart(prev => addDays(prev, direction * 7));
  };

  const getWeekDates = () => {
    if (!currentWeekStart) return [];
    return DAYS.map((_, i) => addDays(currentWeekStart, i));
  };

  const weekDates = getWeekDates();
  const weekStartStr = currentWeekStart ? format(currentWeekStart, 'yyyy-MM-dd') : '';
  const weekEndStr = currentWeekStart ? format(addDays(currentWeekStart, 6), 'MMM dd') : '';

  // Get attendance for a specific user and date
  const getUserAttendance = (email, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return attendance.find(a => a.user_email === email && a.date === dateStr);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Shift Roster</h1>
          <p className="text-slate-500">
            {canManage ? 'Manage weekly shifts and track attendance' : 'View your shifts and log attendance'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!todayAttendance?.login_time ? (
            <Button onClick={handleLogin} className="bg-emerald-600 hover:bg-emerald-700">
              <LogIn className="mr-2 h-4 w-4" /> Clock In
            </Button>
          ) : !todayAttendance?.logout_time ? (
            <Button onClick={handleLogout} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
              <LogOut className="mr-2 h-4 w-4" /> Clock Out
            </Button>
          ) : (
            <Badge className="bg-slate-100 text-slate-600 py-2 px-4">
              <Clock className="mr-2 h-4 w-4" /> Logged: {todayAttendance.total_hours || 0}h
            </Badge>
          )}
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Today's Status */}
      {todayAttendance && (
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
                  <Clock className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-emerald-800">Today's Attendance</p>
                  <p className="text-sm text-emerald-600">
                    {todayAttendance.scheduled_shift ? `Scheduled: ${SHIFT_LABELS[todayAttendance.scheduled_shift] || todayAttendance.scheduled_shift}` : 'No shift scheduled'}
                  </p>
                </div>
              </div>
              <div className="flex gap-6 text-sm">
                <div>
                  <p className="text-emerald-600">Login</p>
                  <p className="font-medium">{todayAttendance.login_time ? format(parseISO(todayAttendance.login_time), 'hh:mm a') : '-'}</p>
                </div>
                <div>
                  <p className="text-emerald-600">Logout</p>
                  <p className="font-medium">{todayAttendance.logout_time ? format(parseISO(todayAttendance.logout_time), 'hh:mm a') : '-'}</p>
                </div>
                <div>
                  <p className="text-emerald-600">Tickets</p>
                  <p className="font-medium">{todayAttendance.tickets_resolved || 0} / {todayAttendance.tickets_assigned || 0}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="roster" className="space-y-4">
        <TabsList>
          <TabsTrigger value="roster" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" /> Weekly Roster
          </TabsTrigger>
          {canManage && (
            <TabsTrigger value="attendance" className="flex items-center gap-2">
              <Clock className="h-4 w-4" /> Attendance Log
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="roster">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Weekly Roster</CardTitle>
                  <CardDescription>
                    {currentWeekStart && `Week of ${format(currentWeekStart, 'MMM dd')} - ${weekEndStr}`}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}>
                    Today
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  {canManage && (
                    <Button onClick={handleSaveRoster} className="ml-4 bg-slate-900 hover:bg-slate-800">
                      <Save className="mr-2 h-4 w-4" /> Save Roster
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-slate-500">Loading...</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-semibold min-w-[150px]">Employee</TableHead>
                        {weekDates.map((date, i) => (
                          <TableHead key={i} className={`text-center min-w-[100px] ${isToday(date) ? 'bg-blue-50' : ''}`}>
                            <div className="font-semibold">{DAY_LABELS[i]}</div>
                            <div className="text-xs text-slate-500">{format(date, 'MMM dd')}</div>
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(canManage ? users : weeklyRoster).map((item, index) => {
                        const userEmail = item.email || item.user_email;
                        const userName = item.full_name || item.user_name || userEmail;
                        const userShifts = editingRoster[userEmail] || {};
                        
                        return (
                          <TableRow key={userEmail || index}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{userName}</p>
                                <p className="text-xs text-slate-500">{userEmail}</p>
                              </div>
                            </TableCell>
                            {DAYS.map((day, dayIndex) => {
                              const shift = userShifts[day] || 'general';
                              const date = weekDates[dayIndex];
                              const att = canManage ? getUserAttendance(userEmail, date) : null;
                              
                              return (
                                <TableCell key={day} className={`text-center ${isToday(date) ? 'bg-blue-50' : ''}`}>
                                  {canManage ? (
                                    <Select
                                      value={shift}
                                      onValueChange={(v) => handleShiftChange(userEmail, day, v)}
                                    >
                                      <SelectTrigger className={`h-8 text-xs ${SHIFT_COLORS[shift]}`}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="morning">Morning (6 AM)</SelectItem>
                                        <SelectItem value="afternoon_12">Afternoon (12 PM)</SelectItem>
                                        <SelectItem value="afternoon_2">Afternoon (2 PM)</SelectItem>
                                        <SelectItem value="general">General (9 AM)</SelectItem>
                                        <SelectItem value="off">Day Off</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <Badge className={SHIFT_COLORS[shift]}>
                                      {SHIFT_LABELS[shift]?.split(' ')[0] || shift}
                                    </Badge>
                                  )}
                                  {att && (
                                    <div className="text-xs mt-1 text-slate-500">
                                      {att.total_hours ? `${att.total_hours}h` : att.login_time ? 'Active' : ''}
                                    </div>
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                      {(canManage ? users : weeklyRoster).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                            <Users className="mx-auto h-12 w-12 text-slate-300 mb-2" />
                            {canManage ? 'No users found. Add employees first.' : 'No roster assigned for this week.'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Shift Legend */}
              <div className="mt-6 pt-4 border-t">
                <p className="text-sm font-medium text-slate-700 mb-2">Shift Legend (9 hours each)</p>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(SHIFT_LABELS).map(([key, label]) => (
                    <Badge key={key} className={`${SHIFT_COLORS[key]} text-xs`}>
                      {label}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {canManage && (
          <TabsContent value="attendance">
            <Card>
              <CardHeader>
                <CardTitle>Attendance Log</CardTitle>
                <CardDescription>
                  Track employee login/logout and performance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Employee</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Shift</TableHead>
                      <TableHead>Login</TableHead>
                      <TableHead>Logout</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead>Tickets</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attendance.length > 0 ? attendance.map((record) => (
                      <TableRow key={record.attendance_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{record.user_name}</p>
                            <p className="text-xs text-slate-500">{record.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{record.date}</TableCell>
                        <TableCell>
                          {record.scheduled_shift && (
                            <Badge className={SHIFT_COLORS[record.scheduled_shift] || 'bg-slate-100'}>
                              {SHIFT_LABELS[record.scheduled_shift]?.split(' ')[0] || record.scheduled_shift}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {record.login_time ? format(parseISO(record.login_time), 'hh:mm a') : '-'}
                        </TableCell>
                        <TableCell>
                          {record.logout_time ? format(parseISO(record.logout_time), 'hh:mm a') : '-'}
                        </TableCell>
                        <TableCell>
                          {record.total_hours ? `${record.total_hours}h` : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <TicketIcon className="h-3 w-3 text-slate-400" />
                            <span className="text-emerald-600">{record.tickets_resolved || 0}</span>
                            <span className="text-slate-400">/</span>
                            <span>{record.tickets_assigned || 0}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            record.status === 'present' ? 'bg-emerald-100 text-emerald-700' :
                            record.status === 'late' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }>
                            {record.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                          No attendance records for this week
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
